import { CanvasObject }  from './canvas.object';
import { Orientation }   from './orientation';
import { Circle }        from './visuals/circle';
import { Line }          from './visuals/line';
import { Logger }        from '@/util/logger';
import { VisualFactory } from './visuals/visual.factory';
import {OnChangedInterface} from '@/helpers/on.changed.interface';

/**
 *
 * @class Port
 *
 * the anchor / port where a route connector can attach to a track
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      January 2024
 *
 */
export class Port extends CanvasObject {

  /** line starting x relative to parent's top left corner */
  public offsetX: number          = 0;

  /** line starting y relative to parent's top left corner */
  public offsetY: number          = 0;

  /** the padding inside the outline */
  public padding: number          = 8;

  /** color */
  public color: string            = 'white';

  /** transparency */
  public opacity: number          = 1.0;

  /** visibility */
  public visible: boolean         = true;

  /** the size of the "dot" */
  public size: number             = 14;

  /** the width of the wire to the "dot" */
  public lineWidth: number        = 5;

  /** the direction the label points (to where a connector can attach) */
  public orientation: Orientation = Orientation.RIGHT;

  /** horizontal position of the "dot" */
  public dotX: number             = 0;

  /** vertical position of the "dot" */
  public dotY: number             = 0;

  /** the wire from the text label to the "dot" */
  public wire: Line | null        = null;

  /** the "dot" that connection can be made to */
  public dot: Circle | null       = null;

  /** notify these watchers if the port positioning changes */
  private changeWatchers: OnChangedInterface[] = [];

  /**
   *
   * @param parent CanvasObject normally should be a link block
   * @param config Partial<BlockTitle> override the style options
   *
   */
  constructor(
    parent: CanvasObject,
    config: Partial<Port> = {}) {

    super(parent.canvas(), true);

    /* link up with parent */

    parent.addChildren(this);

    /* use any overrides */

    Object.assign(this, config);

    Logger.debug(`Port ctor() config: `, config);

    this.wire = VisualFactory.buildLine(this.wireConfig());

    if (this.wire) {

      /* save the handle, so we can use it to manipulate the visual later */

      this.wire.handle = this.canvas()?.addShape(this.wire);

      this.boundUpdate({
        x:      this.wire.x      ?? 0,
        y:      this.wire.y      ?? 0,
        width:  Math.max(this.width,  this.wire.width  ?? 10),
        height: Math.max(this.height, this.wire.height ?? 10)
      });
    }

    this.dot = VisualFactory.buildCircle(this.dotConfig());

    if (this.dot) {

      /* save the handle, so we can use it to manipulate the visual later */

      this.dot.handle = this.canvas()?.addShape(this.dot);

      if (this.orientation == Orientation.RIGHT || this.orientation == Orientation.LEFT) {

        this.width  = this.padding * 3 + this.size;
        this.height = Math.max(this.size, this.lineWidth);

      } else if (this.orientation == Orientation.TOP || this.orientation == Orientation.BOTTOM) {

        this.width  = Math.max(this.size, this.lineWidth);
        this.height = this.padding * 3 + this.size;

      }

    }
  }

  /** register a client to notify when our positioning changes */
  public registerWatcher(x: OnChangedInterface): Port {

    this.changeWatchers.push(x);

    return this;
  }

  /** generate the configuration for the title text label */
  private wireConfig(): Partial<Line> {

    let width: number;
    let height: number;
    let points: {x: number, y: number}[]  = [];
    let color: string                     = this.color;
    let lineWidth: number                 = this.lineWidth;
    let visible: boolean                  = true;
    let opacity: number                   = 1.0;

    let x1: number = this.offsetX;
    let y1: number = this.offsetY;
    let x2: number = x1;
    let y2: number = y1;

    /*
     * (x2,y2) is where the line ends / points to, depending on our orientation;
     * basically has to point towards the center of the window, so that connections
     * can be made from other blocks (the blocks are at the edges of the window).
     *
     */

    if (this.orientation == Orientation.RIGHT) {

      /* pointing to the right */

      x1 = x1 + this.padding;
      x2 = x1 + this.padding * 3;
      y2 = y1;

    } else if (this.orientation == Orientation.LEFT) {

      /* pointing left */

      x1 = x1 - this.padding;
      x2 = x1 - this.padding * 3;
      y2 = y1;

    } else if (this.orientation == Orientation.TOP) {

      /* pointing up */

      y1 = y1 - this.padding;
      x2 = x1;
      y2 = y1 - this.padding * 2;

    } else if (this.orientation == Orientation.BOTTOM) {

      /* pointing down */

      y1 = y1 + this.padding;
      x2 = x1;
      y2 = y1 + this.padding * 2;
    }

    points.push({x: x1, y: y1});
    points.push({x: x2, y: y2});

    this.dotX = x2;
    this.dotY = y2;

    width     = Math.abs(x2 - x1);
    height    = Math.abs(y2 - y1);

    /* update any change watchers */

    this.changeWatchers.forEach((x: OnChangedInterface) => x.onChange());

    /* pass back the specification */

    return {
      x: x1,
      y: y1,
      width,
      height,
      points,
      color,
      lineWidth,
      visible,
      opacity
    };
  }

  private dotConfig(): Partial<Circle> {

    let x: number                = this.dotX;
    let y: number                = this.dotY;
    let width: number            = this.size;
    let height: number           = this.size;
    let radius: number           = this.size / 2;
    let backgroundColor: string  = this.color;
    let borderColor: string      = this.color;
    let borderWidth: number      = 1;
    let visible: boolean         = true;
    let opacity: number          = 1.0;

    return {
      x,
      y,
      width,
      height,
      radius,
      backgroundColor,
      borderColor,
      borderWidth,
      visible,
      opacity
    };
  }

  /** upon return object should have updated its layout or geometry as needed. */
  public layout(): CanvasObject {

    /*
     * the screen geometry has changed somehow, so we have to recalculate our layout and
     * then get the canvas visuals to update in place.
     *
     */

    /* rebuild the wire */

    let handle: any = this.wire?.handle ?? null;

    if (!handle) {

      /* nothing to do layout on yet */

      return this;
    }

    this.wire        = VisualFactory.buildLine(this.wireConfig());
    this.wire.handle = handle;

    this.boundUpdate({
      x:      this.wire.x      ?? 0,
      y:      this.wire.y      ?? 0,
      width:  Math.max(this.width,  this.wire.width  ?? 10),
      height: Math.max(this.height, this.wire.height ?? 10)
    });

    this.canvas()?.updateShape(this.wire);

    /* rebuild the dot */

    handle =  this.dot?.handle ?? null;

    this.dot = VisualFactory.buildCircle(this.dotConfig());
    this.dot.handle = handle;

    /* save the handle, so we can use it to manipulate the visual later */

    if (this.orientation == Orientation.RIGHT || this.orientation == Orientation.LEFT) {

      this.width  = this.padding * 3 + this.size;
      this.height = Math.max(this.size, this.lineWidth);

    } else if (this.orientation == Orientation.TOP || this.orientation == Orientation.BOTTOM) {

      this.width  = Math.max(this.size, this.lineWidth);
      this.height = this.padding * 3 + this.size;

    }

    this.canvas()?.updateShape(this.dot);

    return this;
  }
}
